/**
 * Makes a lucide icon from the text of an .svg file, so that an icon is replaced by a replacement
 * of its file. The icon takes the size, stroke and color of the lucide icons around it: the
 * attributes of the root <svg> are dropped, and so are the line and color attributes of each
 * shape.
 */

import type { IconNode, LucideIcon } from 'lucide-react';
import { createLucideIcon } from 'lucide-react';

const SHAPE_TAGS = new Set(['path', 'circle', 'ellipse', 'line', 'polygon', 'polyline', 'rect']);

// The root and the closing tags of the shapes, which carry nothing to draw.
const IGNORED_TAGS = new Set(['svg', '/svg', ...[...SHAPE_TAGS].map((tag) => `/${tag}`)]);

// A shape inherits these from the root, so that a CSS rule on the icon, such as a thinner line in
// the nav rail, reaches each shape. An attribute on a shape would win over the inherited value.
const ROOT_ATTRIBUTES = new Set(['color', 'stroke', 'stroke-width', 'stroke-linecap',
    'stroke-linejoin']);

function toCamelCase(name: string): string {
    return name.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());
}

/** The shapes of an .svg file. Throws on an element that the icon cannot draw, such as a <g>. */
export function parseSvgShapes(svg: string): IconNode {
    const shapes: IconNode = [];
    for (const [, tag = '', body = ''] of svg.matchAll(/<(\/?[\w:-]+)([^>]*)>/g)) {
        if (IGNORED_TAGS.has(tag)) {
            continue;
        }
        if (!SHAPE_TAGS.has(tag)) {
            throw new Error(`svgIcon: <${tag}> is not supported; use only ${[...SHAPE_TAGS]}`);
        }
        const attrs: Record<string, string> = { key: `${tag}-${shapes.length}` };
        const pairs = body.matchAll(/([\w:-]+)=(?:"([^"]*)"|'([^']*)')/g);
        for (const [, name = '', doubleQuoted, singleQuoted = ''] of pairs) {
            if (ROOT_ATTRIBUTES.has(name)) {
                continue;
            }
            const value = doubleQuoted ?? singleQuoted;
            // A filled shape takes the color of the line, as the stroke does.
            attrs[toCamelCase(name)] = name === 'fill' && value !== 'none'
                ? 'currentColor'
                : value;
        }
        shapes.push([tag as IconNode[number][0], attrs]);
    }
    return shapes;
}

export function makeSvgIcon(name: string, svg: string): LucideIcon {
    return createLucideIcon(name, parseSvgShapes(svg));
}
